import React, { useEffect, useMemo, useState } from "react";
import {
    Container,
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    Chip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Snackbar,
    Alert,
    CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AddIcon from '@mui/icons-material/Add';
import { Link } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import { isAuthenticated } from "../auth";
import { createPastEvent, listPastEvents } from '../event/apiEvents';
import Footer from '../core/Footer';

const ADD_EVENT_USER_ID = '5e2611483f99fa3bb4dd922a';

const emptyForm = {
    title: '',
    location: '',
    startDate: '',
    endDate: '',
    shortDescription: '',
    longDescription: '',
};

const YEAR_PALETTE = ['#FF6600', '#003E7E'];

// Pull a 4-digit year out of a free-form date field, falling back to createdAt.
const extractYear = (e) => {
    const candidates = [e.startDate, e.endDate, e.createdAt];
    for (const c of candidates) {
        if (!c) continue;
        const m = String(c).match(/\b(19|20)\d{2}\b/);
        if (m) return m[0];
    }
    return 'Other';
};

const pastEvents = [
    {
        year: '2025',
        label: 'Chattanooga, TN',
        color: '#FF6600',
        events: [
            {
                title: '2025 SDSIC Conference — Chattanooga',
                location: 'Chattanooga, TN',
                date: 'Fall 2025',
                image: '',
                link: '/showcaseEvent/673c6cb712555006f0f99b3a',
                requiresAuth: true,
                description: 'Our annual fall conference featuring industry presentations, workshops, and networking.',
            }            
        ],
    },
    {
        year: '2024',
        label: 'Kansas City',
        color: '#003E7E',
        events: [
            {
                title: '2024 SDSIC Conference — Kansas City',
                location: 'Kansas City, MO',
                date: 'Fall 2024',
                link: '/showcaseEvent/655bb358b24d7dfa488b46af',
                requiresAuth: true,
                description: 'Annual conference with committee presentations, vendor showcases, and award ceremonies.',
            }
        ],
    },
    {
        year: '2023',
        label: 'Nashville & Virtual',
        color: '#FF6600',
        events: [
            {
                title: '2023 Fall Virtual Conference',
                location: 'Virtual',
                date: 'Fall 2023',
                link: '/showcaseEvent/655b7faed1c936a37548aad9',
                requiresAuth: true,
                description: 'Virtual fall conference with presentations and interactive sessions.',
            },
            {
                title: '2023 SDSIC Conference — Nashville',
                location: 'Nashville, TN',
                date: 'Spring 2023',
                link: '/showcaseEvent/6380ae5f12188c63625e632d',
                requiresAuth: true,
                description: 'In-person spring conference in Music City.',
            }
        ],
    },
    {
        year: '2022',
        label: 'Spring & Fall',
        color: '#003E7E',
        events: [
            {
                title: '2022 Fall Conference',
                date: 'Fall 2022',
                link: '/showcaseEvent/6261a6116b020bed17a90d48',
                requiresAuth: true,
                description: 'Annual fall conference with industry-leading presentations.',
            },
            {
                title: '2022 Spring Conference',
                date: 'Spring 2022',
                link: '/showcaseEvent/62054cf16f4c6a6808384c1e',
                requiresAuth: true,
                description: 'Spring event featuring committee updates and vendor presentations.',
            }
        ],
    },
    {
        year: '2021',
        label: 'Spring & Fall',
        color: '#FF6600',
        events: [
            {
                title: '2021 Fall Conference',
                date: 'Fall 2021',
                link: '/showcaseEvent/613f4a91fc90ef699581b27d',
                requiresAuth: true,
                description: 'Fall conference with expanded virtual attendance options.',
            },
            {
                title: '2021 Spring Conference',
                date: 'Spring 2021',
                link: '/showcaseEvent/6001c0547024a75b1a789ab1',
                requiresAuth: true,
                description: 'Spring conference marking the return of in-person events.',
            },
        ],
    },
];

const PastEvents = () => {
    const [expanded, setExpanded] = useState('panel-2026');
    const auth = isAuthenticated();
    const authed = !!auth;
    const canAddEvent = authed && auth.userId === ADD_EVENT_USER_ID;

    const [addOpen, setAddOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [createError, setCreateError] = useState('');
    const [createdEvent, setCreatedEvent] = useState(null);
    const [dynamicEvents, setDynamicEvents] = useState([]);

    const loadDynamicEvents = () => {
        listPastEvents().then((data) => {
            if (Array.isArray(data)) setDynamicEvents(data);
        });
    };

    useEffect(() => { loadDynamicEvents(); }, []);

    // Merge dynamic admin-created events into the hardcoded year groups,
    // skipping any whose _id is already in the curated list.
    const mergedYearGroups = useMemo(() => {
        const hardcodedIds = new Set();
        pastEvents.forEach(g => g.events.forEach(e => {
            const id = (e.link || '').split('/').pop();
            if (id) hardcodedIds.add(id);
        }));

        const groups = pastEvents.map(g => ({ ...g, events: [...g.events] }));
        const yearIndex = new Map(groups.map((g, i) => [g.year, i]));

        dynamicEvents.forEach(e => {
            if (hardcodedIds.has(e._id)) return;
            const year = extractYear(e);
            const card = {
                title: e.title,
                location: e.location || '',
                date: e.startDate || '',
                link: `/showcaseEvent/${e._id}`,
                requiresAuth: true,
                description: e.shortDescription || '',
            };
            if (yearIndex.has(year)) {
                groups[yearIndex.get(year)].events.unshift(card);
            } else {
                groups.unshift({
                    year,
                    label: 'Recently Added',
                    color: YEAR_PALETTE[groups.length % YEAR_PALETTE.length],
                    events: [card],
                });
                yearIndex.set(year, 0);
                // shift existing indices since we unshifted
                for (const [k, v] of yearIndex) {
                    if (k !== year) yearIndex.set(k, v + 1);
                }
            }
        });

        return groups.sort((a, b) => {
            if (a.year === 'Other') return 1;
            if (b.year === 'Other') return -1;
            return parseInt(b.year, 10) - parseInt(a.year, 10);
        });
    }, [dynamicEvents]);

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleFormChange = (field) => (e) => {
        setForm({ ...form, [field]: e.target.value });
    };

    const openAddDialog = () => {
        setForm(emptyForm);
        setCreateError('');
        setAddOpen(true);
    };

    const closeAddDialog = () => {
        if (saving) return;
        setAddOpen(false);
    };

    const submitNewEvent = () => {
        if (!form.title.trim()) {
            setCreateError('Title is required.');
            return;
        }
        setSaving(true);
        setCreateError('');
        const eventInfo = {
            title: form.title.trim(),
            location: form.location.trim(),
            startDate: form.startDate.trim(),
            endDate: form.endDate.trim(),
            shortDescription: form.shortDescription.trim(),
            longDescription: form.longDescription.trim(),
        };
        const newEventId = uuidv4();
        createPastEvent(eventInfo, newEventId).then((resp) => {
            setSaving(false);
            if (!resp || resp.error) {
                setCreateError((resp && resp.error) || 'Could not create event.');
                return;
            }
            const saved = resp.data || resp;
            setCreatedEvent(saved);
            setAddOpen(false);
            setForm(emptyForm);
            loadDynamicEvents();
        });
    };

    return (
        <>
            {/* Hero Section */}
            <Box sx={{ backgroundColor: '#003E7E', color: 'white', py: 6, mb: 4 }}>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <EventIcon sx={{ fontSize: { xs: 40, sm: 64 } }} />
                        <Box>
                            <Typography variant="h2" component="h1" sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3.75rem' } }}>
                                Past Events
                            </Typography>
                            <Typography variant="h5" sx={{ opacity: 0.9, fontSize: { xs: '1rem', sm: '1.5rem' } }}>
                                A look back at SDSIC conferences and gatherings
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ mb: 6 }}>
                {/* Photo Gallery Link + (admin) Add Event */}
                <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                        component={Link}
                        to="/events"
                        variant="outlined"
                        startIcon={<PhotoLibraryIcon />}
                        sx={{
                            borderColor: '#003E7E',
                            color: '#003E7E',
                            fontWeight: 600,
                            '&:hover': { borderColor: '#FF6600', color: '#FF6600' },
                        }}
                    >
                        View Live Event Photos
                    </Button>

                    {canAddEvent && (
                        <Button
                            onClick={openAddDialog}
                            variant="contained"
                            startIcon={<AddIcon />}
                            sx={{
                                backgroundColor: '#FF6600',
                                color: 'white',
                                fontWeight: 600,
                                '&:hover': { backgroundColor: '#cc5200' },
                            }}
                        >
                            Add Event
                        </Button>
                    )}
                </Box>

                {/* Year Accordions */}
                {mergedYearGroups.map((yearGroup) => {
                    const panelId = `panel-${yearGroup.year}`;
                    return (
                        <Accordion
                            key={panelId}
                            expanded={expanded === panelId}
                            onChange={handleChange(panelId)}
                            elevation={3}
                            sx={{
                                mb: 3,
                                borderRadius: 3,
                                overflow: 'hidden',
                                '&:before': { display: 'none' },
                                '&.Mui-expanded': { margin: '0 0 24px 0' },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ fontSize: 32, color: yearGroup.color }} />}
                                sx={{
                                    backgroundColor: expanded === panelId ? yearGroup.color : 'rgba(0, 62, 126, 0.02)',
                                    color: expanded === panelId ? 'white' : '#003E7E',
                                    minHeight: { xs: 60, sm: 80 },
                                    '&.Mui-expanded': { minHeight: { xs: 60, sm: 80 } },
                                    '& .MuiAccordionSummary-content': {
                                        alignItems: 'center',
                                        gap: { xs: 1, sm: 2 },
                                        my: { xs: 1, sm: 2 },
                                        flexWrap: 'wrap',
                                    },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                <EventIcon sx={{ fontSize: { xs: 24, sm: 36 }, color: expanded === panelId ? 'white' : yearGroup.color }} />
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                                        {yearGroup.year}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                        {yearGroup.label}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={`${yearGroup.events.length} event${yearGroup.events.length > 1 ? 's' : ''}`}
                                    size="small"
                                    sx={{
                                        ml: 'auto',
                                        mr: 2,
                                        backgroundColor: expanded === panelId ? 'rgba(255,255,255,0.2)' : yearGroup.color + '15',
                                        color: expanded === panelId ? 'white' : yearGroup.color,
                                        fontWeight: 600,
                                    }}
                                />
                            </AccordionSummary>

                            <AccordionDetails sx={{ p: 4 }}>
                                <Grid container spacing={3}>
                                    {yearGroup.events.map((event, idx) => {
                                        const canAccess = !event.requiresAuth || authed;
                                        return (
                                            <Grid item xs={12} sm={6} md={4} key={idx}>
                                                <Card
                                                    elevation={2}
                                                    sx={{
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        borderRadius: 3,
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': {
                                                            transform: 'translateY(-6px)',
                                                            boxShadow: `0 12px 24px ${yearGroup.color}30`,
                                                        },
                                                    }}
                                                >
                                                    {event.image && (
                                                        <CardMedia
                                                            component="img"
                                                            image={event.image}
                                                            alt={event.title}
                                                            sx={{
                                                                height: 180,
                                                                objectFit: 'cover',
                                                                borderBottom: `4px solid ${yearGroup.color}`,
                                                            }}
                                                        />
                                                    )}
                                                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#003E7E', mb: 1 }}>
                                                            {event.title}
                                                        </Typography>

                                                        {event.location && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                                                <LocationOnIcon sx={{ fontSize: 16, color: '#666' }} />
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {event.location}
                                                                </Typography>
                                                            </Box>
                                                        )}

                                                        {event.date && (
                                                            <Chip
                                                                label={event.date}
                                                                size="small"
                                                                sx={{
                                                                    mb: 2,
                                                                    backgroundColor: yearGroup.color + '15',
                                                                    color: yearGroup.color,
                                                                    fontWeight: 600,
                                                                }}
                                                            />
                                                        )}

                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                            {event.description}
                                                        </Typography>

                                                        <Button
                                                            component={Link}
                                                            to={canAccess ? event.link : '/signin'}
                                                            variant="outlined"
                                                            size="small"
                                                            endIcon={<OpenInNewIcon />}
                                                            sx={{
                                                                borderColor: yearGroup.color,
                                                                color: yearGroup.color,
                                                                '&:hover': {
                                                                    borderColor: yearGroup.color,
                                                                    backgroundColor: yearGroup.color + '10',
                                                                },
                                                            }}
                                                        >
                                                            {canAccess ? 'View Event' : 'Sign in to View'}
                                                        </Button>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Container>

            {/* Add Event dialog (visible only to ADD_EVENT_USER_ID) */}
            <Dialog open={addOpen} onClose={closeAddDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: '#003E7E', fontWeight: 600 }}>Add New Event</DialogTitle>
                <DialogContent dividers>
                    {createError && (
                        <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>
                    )}
                    <TextField
                        label="Title"
                        fullWidth
                        required
                        margin="dense"
                        value={form.title}
                        onChange={handleFormChange('title')}
                    />
                    <TextField
                        label="Location"
                        fullWidth
                        margin="dense"
                        placeholder="e.g. Chattanooga, TN"
                        value={form.location}
                        onChange={handleFormChange('location')}
                    />
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Start Date"
                                fullWidth
                                margin="dense"
                                placeholder="e.g. Fall 2025 or 2025-10-01"
                                value={form.startDate}
                                onChange={handleFormChange('startDate')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="End Date"
                                fullWidth
                                margin="dense"
                                placeholder="e.g. 2025-10-04"
                                value={form.endDate}
                                onChange={handleFormChange('endDate')}
                            />
                        </Grid>
                    </Grid>
                    <TextField
                        label="Short Description"
                        fullWidth
                        margin="dense"
                        inputProps={{ maxLength: 100 }}
                        helperText="Up to 100 characters"
                        value={form.shortDescription}
                        onChange={handleFormChange('shortDescription')}
                    />
                    <TextField
                        label="Long Description"
                        fullWidth
                        margin="dense"
                        multiline
                        minRows={3}
                        inputProps={{ maxLength: 2000 }}
                        value={form.longDescription}
                        onChange={handleFormChange('longDescription')}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeAddDialog} disabled={saving}>Cancel</Button>
                    <Button
                        onClick={submitNewEvent}
                        variant="contained"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ backgroundColor: '#003E7E', '&:hover': { backgroundColor: '#002a57' } }}
                    >
                        {saving ? 'Creating...' : 'Create Event'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!createdEvent}
                autoHideDuration={8000}
                onClose={() => setCreatedEvent(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setCreatedEvent(null)}
                    severity="success"
                    sx={{ width: '100%' }}
                    action={createdEvent && createdEvent._id ? (
                        <Button
                            color="inherit"
                            size="small"
                            component={Link}
                            to={`/showcaseEvent/${createdEvent._id}`}
                        >
                            View
                        </Button>
                    ) : null}
                >
                    Event created successfully.
                </Alert>
            </Snackbar>

            <Footer />
        </>
    );
};

export default PastEvents;
